/**
 * Proactive Health Monitor
 *
 * A lightweight background watchdog that detects resource exhaustion
 * *before* it crashes the app and takes corrective action automatically.
 *
 * Checks (every 30 s):
 * 1. Storage pressure — prune at 70 %, emergency cleanup at 90 %
 * 2. Memory pressure — flush caches when JS heap > 80 % (Chrome only)
 * 3. AudioContext state — auto-reset if closed/interrupted
 * 4. FPS watchdog — enable battery-saver when sustained < 5 fps
 *
 * Plus one-time setup:
 * 5. Global WebGL context-loss listener (fallback rendering)
 *
 * Privacy: purely on-device, no network traffic.
 */

import { getStoragePressure, emergencyCleanup } from '@/lib/safeStorage';
import { getAudioContextState, resetSharedAudioContext } from '@/lib/audioContextPool';
import { isBatterySaver, setBatterySaver } from '@/lib/performanceSettings';
import { clearToneCache } from '@/lib/toneCache';

// ── FPS tracking ──

let _lastFrameTime = 0;
let _frameTimes: number[] = [];
let _rafId = 0;
let _lowFpsStart = 0;
const LOW_FPS_THRESHOLD = 5;
const LOW_FPS_DURATION_MS = 5_000;

function fpsLoop(now: number) {
  if (_lastFrameTime > 0) {
    _frameTimes.push(now - _lastFrameTime);
    if (_frameTimes.length > 90) _frameTimes.shift(); // keep ~3 s at 30 fps
  }
  _lastFrameTime = now;
  _rafId = requestAnimationFrame(fpsLoop);
}

export function getAverageFps(): number {
  if (_frameTimes.length < 5) return 60; // not enough data
  const avg = _frameTimes.reduce((a, b) => a + b, 0) / _frameTimes.length;
  return avg > 0 ? 1000 / avg : 60;
}

// ── Storage pressure ──

function checkStorage() {
  try {
    const pressure = getStoragePressure();
    if (pressure >= 0.9) {
      console.warn(`[HealthMonitor] Storage at ${Math.round(pressure * 100)}% — emergency cleanup`);
      emergencyCleanup();
    } else if (pressure >= 0.7) {
      console.log(`[HealthMonitor] Storage at ${Math.round(pressure * 100)}% — soft prune`);
      // Soft prune: only the largest non-essential keys
      const softKeys = [
        'aphenis_crash_log', 'aphenis_xp_log', 'aphenis_game_history',
        'aphenis_voice_transcriptions', 'aphenis_pet_activity_feed',
        'aphenis_petworld_checkpoint',
      ];
      for (const key of softKeys) {
        try { localStorage.removeItem(key); } catch {}
      }
    }
  } catch {}
}

// ── Memory pressure (Chrome / Android only) ──

function checkMemory() {
  try {
    const mem = (performance as any).memory;
    if (!mem) return;
    const ratio = mem.usedJSHeapSize / mem.jsHeapSizeLimit;
    if (ratio > 0.8) {
      console.warn(`[HealthMonitor] JS heap at ${Math.round(ratio * 100)}% — flushing caches`);
      // Flush tone audio buffer caches
      try { clearToneCache(); } catch {}
    }
  } catch {}
}

// ── AudioContext health ──

function checkAudioContext() {
  try {
    const state = getAudioContextState();
    if (state === 'closed' || (state as string) === 'interrupted') {
      console.warn(`[HealthMonitor] AudioContext is ${state} — resetting`);
      resetSharedAudioContext();
    }
  } catch {}
}

// ── FPS watchdog ──

function checkFps() {
  try {
    if (isBatterySaver()) return; // already in lowest mode
    const fps = getAverageFps();
    if (fps < LOW_FPS_THRESHOLD) {
      if (_lowFpsStart === 0) {
        _lowFpsStart = Date.now();
      } else if (Date.now() - _lowFpsStart > LOW_FPS_DURATION_MS) {
        console.warn(`[HealthMonitor] Sustained low FPS (${fps.toFixed(1)}) — activating battery saver`);
        setBatterySaver(true);
        _lowFpsStart = 0;
        // Stop the RAF loop since we no longer need to monitor
        if (_rafId) { cancelAnimationFrame(_rafId); _rafId = 0; }
      }
    } else {
      _lowFpsStart = 0; // reset
    }
  } catch {}
}

// ── WebGL global context-loss listener ──

function initWebGLGuard() {
  try {
    document.addEventListener('webglcontextlost', (e) => {
      const canvas = e.target as HTMLCanvasElement | null;
      const id = canvas?.id || canvas?.className || 'unknown';
      console.warn(`[HealthMonitor] WebGL context lost on canvas: ${id}`);
      // The individual components should handle their own recovery,
      // but as a safety net we mark the canvas so CSS can show a fallback
      if (canvas) {
        canvas.dataset.contextLost = 'true';
        canvas.style.display = 'none';
      }
    }, true); // capture phase to catch all canvases
  } catch {}
}

// ── Unified tick ──

let _tickInterval: ReturnType<typeof setInterval> | null = null;
let _lastTickTime = 0;

function healthTick() {
  _lastTickTime = Date.now();
  checkStorage();
  checkAudioContext();
  checkMemory();
  checkFps();
}

/** Whether the health monitor is currently running. */
export function isHealthMonitorRunning(): boolean {
  return _initialized;
}

/** Timestamp of the last health tick (0 if never run). */
export function getLastTickTime(): number {
  return _lastTickTime;
}

// ── Public API ──

let _initialized = false;

/**
 * Start the health monitor. Call once after app render in main.tsx.
 */
export function initHealthMonitor(): void {
  if (_initialized) return;
  _initialized = true;

  // Start FPS monitoring
  _rafId = requestAnimationFrame(fpsLoop);

  // Global WebGL guard
  initWebGLGuard();

  // Unified 30-second tick
  _tickInterval = setInterval(() => {
    try { healthTick(); } catch (err) {
      console.error('[HealthMonitor] Tick error:', err);
    }
  }, 30_000);

  // Run an immediate check
  setTimeout(() => { try { healthTick(); } catch {} }, 5_000);

  console.log('[HealthMonitor] Initialized');
}

/**
 * Run all health checks immediately (called on app resume).
 */
export function runHealthCheckNow(): void {
  try { healthTick(); } catch {}
}
